import type { ActionTree } from "vuex";
import type {
  DataAnalyticsState,
  RootState,
  ExpressionTestResult,
  Scenario,
  DataAnalyticsConfig,
} from "@/types";

import { httpClient, type AxiosResponse } from "@/services/httpClient";

import {
  LOAD_VARIABLES,
  LOAD_VARIABLES_SUCCESS,
  LOAD_VARIABLES_FAILURE,
  UPDATE_MODELIZATION_DATA,
  UPDATE_VARIABLE_TO_MODEL_MODELIZATION_DATA_SUCCESS,
  UPDATE_EXPLICATIVE_VARIABLES_MODELIZATION_DATA_SUCCESS,
  UPDATE_MODELIZATION_RESULT_SUCCESS,
  UPDATE_MODELIZATION_RESULT_PROGRESS,
  UPDATE_MODELIZATION_DATA_FAILURE,
  INIT_EXPLICATIVE_VARIABLES_MODELIZATION_IN_STORE,
  INIT_MODELING_RESULT_STORE,
  INIT_VARIABLE_TO_MODEL_MODELIZATION_IN_STORE,
  INIT_SCENARIO_IN_STORE,
  INIT_PROGRESS_MODELIZATION_TREATMENT_MESSAGE,
  DELETE_EXPLICATIVE_VARIABLE_IN_IN_Store,
  UPDATE_MODELIZATION_RESULT_DATA,
  UPDATE_MODELIZATION_RESULT_DATA_SUCCESS,
  UPDATE_MODELIZATION_RESULT_DATA_ERROR,
  LOAD_SCENARIO_BY_ID,
  LOAD_SCENARIO_BY_ID_SUCCESS,
  LOAD_SCENARIO_BY_ID_FAILURE,
  ADD_SCENARIO,
  ADD_SCENARIO_SUCCESS,
  ADD_SCENARIO_FAILURE,
  UPDATE_SCENARIO,
  UPDATE_SCENARIO_SUCCESS,
  UPDATE_SCENARIO_FAILURE,
  LOAD__DATA_ANALYTICS_CONFIG,
  LOAD__DATA_ANALYTICS_CONFIG_SUCCESS,
  LOAD__DATA_ANALYTICS_CONFIG_FAILURE,
  ADD_DATA_ANALYTICS_CONFIG,
  ADD_DATA_ANALYTICS_CONFIG_SUCCESS,
  ADD_DATA_ANALYTICS_CONFIG_FAILURE,
  UPDATE_DATA_ANALYTICS_CONFIG,
  UPDATE_DATA_ANALYTICS_CONFIG_SUCCESS,
  UPDATE_DATA_ANALYTICS_CONFIG_FAILURE,
  DELETE_DATA_ANALYTICS_CONFIG,
  DELETE_DATA_ANALYTICS_CONFIG_SUCCESS,
  DELETE_DATA_ANALYTICS_CONFIG_FAILURE,
} from "./labels";

import { HOSTS, ENDPOINTS } from "@/constants";
export const actions: ActionTree<DataAnalyticsState, RootState> = {
  //VARIABLES
  fetchVariables({ commit }, params: any) {
    commit(LOAD_VARIABLES);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.VARIABLES}`, {
        params: params,
      })
      .then(
        (response: any) => {
          commit(LOAD_VARIABLES_SUCCESS, {
            variables: response.data.map((variable: any) => {
              return { ...variable, aggregationId: variable.aggregation.id };
            }),
            count: response.total_count,
          });
        },
        (error) => {
          commit(LOAD_VARIABLES_FAILURE, error.message);
        },
      );
  },
  fetchModelizationData({ commit }, params: any) {
    if (params.variable) {
      commit(UPDATE_MODELIZATION_DATA);
      httpClient
        .get(`${HOSTS.AWS}/${ENDPOINTS.GRAPH_DATA}`, {
          params: {
            id: params.variable.object.id,
            aggregationId: params.variable.aggregation.id,
            type: params.variable.type,
            preferredSampling: params.preferredSampling,
            preferredSamplingUnit: params.preferredSamplingUnit,
            start: params.periode[0],
            end: params.periode[1],
            limit: 10000,
            timeserie: true,
          },
        })
        .then(
          (response: AxiosResponse<any>) => {
            if (params.variable.isVariableToModel) {
              commit(UPDATE_VARIABLE_TO_MODEL_MODELIZATION_DATA_SUCCESS, {
                ...params.variable,
                data: response.data.data,
                unit: response.data.unit.name,
                preferredSampling: response.data.sampling,
                preferredSamplingUnit: response.data.samplingUnit,
              });
            }
            if (params.variable.isExplicativesVariables) {
              commit(UPDATE_EXPLICATIVE_VARIABLES_MODELIZATION_DATA_SUCCESS, {
                ...params.variable,
                data: response.data.data,
                unit: response.data.unit.name,
                preferredSampling: response.data.sampling,
                preferredSamplingUnit: response.data.samplingUnit,
              });
            }
          },
          (error) => {
            commit(UPDATE_MODELIZATION_DATA_FAILURE, error.message);
          },
        );
    }
  },
  updateModelingResult({ commit }, data) {
    if (data.status === "IN_PROCESS") {
      commit(UPDATE_MODELIZATION_RESULT_PROGRESS, data.message);
    }

    if (data.status === "ok") {
      commit(UPDATE_MODELIZATION_RESULT_SUCCESS, data.message);
    }
    if (data.status === "error") {
      commit(UPDATE_MODELIZATION_DATA_FAILURE, data.message);
    }
  },

  deleteVariableToModelInStore({ commit }, variable) {
    commit(UPDATE_VARIABLE_TO_MODEL_MODELIZATION_DATA_SUCCESS, variable);
  },
  initExplicativesVariablesInStore({ commit }) {
    commit(INIT_EXPLICATIVE_VARIABLES_MODELIZATION_IN_STORE);
  },
  initModelingResultInStore({ commit }) {
    commit(INIT_MODELING_RESULT_STORE);
  },
  initProgressModelizationTreatmentMessage({ commit }) {
    commit(INIT_PROGRESS_MODELIZATION_TREATMENT_MESSAGE);
  },
  deleteExplicativesVariablesInStore({ commit }, variable) {
    commit(DELETE_EXPLICATIVE_VARIABLE_IN_IN_Store, variable);
  },

  initVariableToModelInStore({ commit }) {
    commit(INIT_VARIABLE_TO_MODEL_MODELIZATION_IN_STORE);
  },
  initScenarioInStore({ commit }) {
    commit(INIT_SCENARIO_IN_STORE);
  },
  fetchFormulaData({ commit }, formula: any) {
    commit(UPDATE_MODELIZATION_RESULT_DATA);
    httpClient.post(`${HOSTS.AWS}/${ENDPOINTS.CHECK_FORMULA}`, formula).then(
      (response: AxiosResponse<ExpressionTestResult>) => {
        commit(UPDATE_MODELIZATION_RESULT_DATA_SUCCESS, response.data.results);
      },
      (error) => {
        commit(UPDATE_MODELIZATION_RESULT_DATA_ERROR, error.message);
      },
    );
  },
  fetchFormulaDataError({ commit }, error: string) {
    commit(UPDATE_MODELIZATION_RESULT_DATA_ERROR, error);
  },

  fetchScenarioById({ commit }, scenarioId: Scenario) {
    commit(LOAD_SCENARIO_BY_ID);
    httpClient.get(`${HOSTS.AWS}/${ENDPOINTS.SCENARIOS}/${scenarioId}`).then(
      (response: AxiosResponse<Scenario>) => {
        commit(LOAD_SCENARIO_BY_ID_SUCCESS, response.data);
      },
      (error) => {
        commit(LOAD_SCENARIO_BY_ID_FAILURE, error.message);
      },
    );
  },
  addScenario({ commit }, scenario: Scenario) {
    commit(ADD_SCENARIO);
    httpClient.post(`${HOSTS.AWS}/${ENDPOINTS.SCENARIOS}`, scenario).then(
      (response: AxiosResponse<Scenario>) => {
        commit(ADD_SCENARIO_SUCCESS, response.data);
      },
      (error) => {
        commit(ADD_SCENARIO_FAILURE, error.message);
      },
    );
  },
  updateScenario({ commit }, scenario: Scenario) {
    commit(UPDATE_SCENARIO);
    httpClient
      .put(`${HOSTS.AWS}/${ENDPOINTS.SCENARIOS}/${scenario?.id}`, {
        ...scenario,
        deletable: undefined,
        updated: undefined,
        id: undefined,
      })
      .then(
        (response: AxiosResponse<Scenario>) => {
          commit(UPDATE_SCENARIO_SUCCESS, response.data);
        },
        (error) => {
          commit(UPDATE_SCENARIO_FAILURE, error.message);
        },
      );
  },
  fetchAnalyticsConfig({ commit }, params: any) {
    commit(LOAD__DATA_ANALYTICS_CONFIG);
    httpClient
      .get(`${HOSTS.AWS}/${ENDPOINTS.ANALYTICS_CONFIG}`, {
        params: params,
      })
      .then(
        (response: AxiosResponse<DataAnalyticsConfig[]>) => {
          commit(LOAD__DATA_ANALYTICS_CONFIG_SUCCESS, response.data);
        },
        (error) => {
          commit(LOAD__DATA_ANALYTICS_CONFIG_FAILURE, error.message);
        },
      );
  },
  deleteDataAnalyticsConfig({ commit }, dataAnalyticsConfigId: string) {
    commit(DELETE_DATA_ANALYTICS_CONFIG);
    httpClient
      .delete(
        `${HOSTS.AWS}/${ENDPOINTS.ANALYTICS_CONFIG}/${dataAnalyticsConfigId}`,
      )
      .then(
        () => {
          commit(DELETE_DATA_ANALYTICS_CONFIG_SUCCESS, dataAnalyticsConfigId);
        },
        (error) => {
          commit(DELETE_DATA_ANALYTICS_CONFIG_FAILURE, error.message);
        },
      );
  },
  updateDataAnalyticsConfig(
    { commit },
    dataAnalyticsConfig: DataAnalyticsConfig,
  ) {
    commit(UPDATE_DATA_ANALYTICS_CONFIG);
    httpClient
      .put(
        `${HOSTS.AWS}/${ENDPOINTS.ANALYTICS_CONFIG}/${dataAnalyticsConfig.id}`,
        {
          ...dataAnalyticsConfig,
          id: undefined,
        },
      )
      .then(
        () => {
          commit(UPDATE_DATA_ANALYTICS_CONFIG_SUCCESS, dataAnalyticsConfig);
        },
        (error) => {
          commit(UPDATE_DATA_ANALYTICS_CONFIG_FAILURE, error.message);
        },
      );
  },
  addDataAnalyticsConfig({ commit }, dataAnalyticsConfig: DataAnalyticsConfig) {
    commit(ADD_DATA_ANALYTICS_CONFIG);
    httpClient
      .post(`${HOSTS.AWS}/${ENDPOINTS.ANALYTICS_CONFIG}`, dataAnalyticsConfig)
      .then(
        (response: AxiosResponse<DataAnalyticsConfig>) => {
          commit(ADD_DATA_ANALYTICS_CONFIG_SUCCESS, response.data);
        },
        (error) => {
          commit(ADD_DATA_ANALYTICS_CONFIG_FAILURE, error.message);
        },
      );
  },
};
